import React, { useEffect, useRef } from "react";
import Slider from "react-slick";
import { Box, GlobalStyles, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Images } from "../../../utils/Images";

const PatientSlider = ({ storyImages = [], taxBenefit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const exemptionTag = taxBenefit || "Tax Benefit";

  // react-slick writes slide widths as inline styles at mount and only
  // recalculates them on a window resize. This carousel lives in a flex column
  // whose width can change without one (web font load, a media-query flip),
  // which leaves the slides too narrow and lets the neighbouring slide show.
  // Watching our own box and firing a resize makes slick re-measure itself.
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let lastWidth = el.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const width = el.getBoundingClientRect().width;
      if (Math.abs(width - lastWidth) < 1) return;
      lastWidth = width;
      window.dispatchEvent(new Event("resize"));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const settings = {
    dots: true,
    infinite: storyImages.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: storyImages.length > 1,
    autoplaySpeed: 3000,
    appendDots: (dots) => (
      <Box
        component="ul"
        sx={{
          m: 0,
          background: "#495858",
          borderRadius: "24px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minWidth: "90px",
          width: "fit-content",
          position: "absolute",
          left: "50%",
          bottom: 24,
          zIndex: 2,
          transform: "translateX(-50%)",
        }}
      >
        {dots}
      </Box>
    ),
        customPaging: () => (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              mx: 0.5,
              background: "#fff",
              border: "none",
              transition: 'all 0.2s',
            }}
          />
        ),
  };

  // Use story images if available, otherwise use fallback banner
  const images = storyImages.length > 0 ? storyImages : [Images.fallBackImageBanner];

  return (
    <>
      <GlobalStyles
        styles={{
  ".slick-slider": {
      height: "100%",
    },
    ".slick-list": {
      height: "100%",
    },
    ".slick-track": {
      height: "100%",
    },
    ".slick-slide": {
      height: "inherit !important",
    },
    ".slick-slide > div": {
      height: "100%",
    },
          ".slick-dots li button:before": {
            content: '""',
            width: 6,
            height: 6,
          },
          ".slick-dots li": {
            margin: 0,
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          },
              ".slick-dots li div": {
                width: 6,
                height: 6,
                transition: 'all 0.2s',
              },
              ".slick-dots li.slick-active div": {
                // Active dot is brand red (was green #b6e04a); the inactive dots
                // stay white so they read against the dark pill behind them.
                background: "#F04951",
                width: 10,
                height: 10,
              },
        }}
      />
      <Box
        ref={containerRef}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          position: "relative",
          width: "100%",
          height: { xs: 250, sm: 350, md: 450, lg: 535 },
          maxHeight: { xs: 250, sm: 350, md: 450, lg: 535 },
        }}
      >
        <Slider {...settings}>
          {images.map((img, idx) => (
            <Box key={idx} sx={{ position: "relative", height: "100%" }}>
              <img
                src={img}
                alt={`story-image-${idx}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "16px",
                }}
              />
            </Box>
          ))}
        </Slider>

        {/* Tax Benefit badge — Figma node 798:6356: 30px/16px padding, 4px
            radius, 16px bold white, red gradient. MUI's <Chip> can't express
            this (fixed 32px height plus its own label padding), which is why it
            rendered small; this is the same Box+Typography the Life detail page
            uses. It sits over the carousel rather than inside each slide — in
            the design it's a sibling of the slides, so it stays put instead of
            sliding along with them. */}
        <Box
          sx={{
            position: "absolute",
            top: isMobile ? "12px" : "20px",
            left: isMobile ? "12px" : "22px",
            zIndex: 3,
            background: "linear-gradient(106.41deg, #EC1B26 5.07%, #FF4A60 106.05%)",
            borderRadius: "4px",
            px: isMobile ? "14px" : "30px",
            py: isMobile ? "8px" : "16px",
            display: "flex",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Red Hat Display",
              fontWeight: 700,
              fontSize: isMobile ? "12px" : "16px",
              lineHeight: 1,
              letterSpacing: "-0.32px",
              color: "#fff",
              whiteSpace: "nowrap",
            }}
          >
            {exemptionTag}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default PatientSlider;
